import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Pencil, Trash2, Check, ChevronLeft, ChevronRight, Settings, X, AlertTriangle, DollarSign, CalendarClock, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Aluguel', 'Internet', 'Telefone', 'Seguros', 'Energia',
  'Ferramentas', 'Software', 'Veículos', 'Impostos', 'Serviços', 'Outros'
];

const CATEGORY_ICONS = {
  'Aluguel': '🏠', 'Internet': '🌐', 'Telefone': '📱', 'Seguros': '🛡️', 'Energia': '⚡',
  'Ferramentas': '🔧', 'Software': '💻', 'Veículos': '🚗', 'Impostos': '📋', 'Serviços': '🔨', 'Outros': '📌'
};

// Ícones dos alertas do Calendário de Alertas (📅 Alertas)
const MY_ALERT_ICONS = {
  seguro: '🛡️', registro: '🔖', manutencao: '🔧', pagamento: '💰', documento: '📄', outro: '📌'
};

const FREQUENCIES = [
  { value: 'daily', label: 'Diária' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'annual', label: 'Anual' },
];

// ====== Calendar Component ======
const BillCalendar = ({ bills, onClickBill, units }) => {
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const monthName = new Date(calYear, calMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const todayStr = new Date().toISOString().slice(0, 10);

  const unitMap = useMemo(() => {
    const m = {};
    (units || []).forEach(u => { m[u.id] = u; });
    return m;
  }, [units]);

  const billsByDay = useMemo(() => {
    const map = {};
    const monthStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}`;
    bills.forEach(b => {
      if (!b.next_due_date) return;
      const due = new Date(b.next_due_date + 'T12:00:00');
      const freq = b.frequency;
      const addUnique = (d) => { if (!map[d]) map[d] = []; if (!map[d].some(x => x.id === b.id)) map[d].push(b); };
      if (b.paid_dates) {
        Object.keys(b.paid_dates).forEach(pd => { if (pd.startsWith(monthStr)) addUnique(parseInt(pd.split('-')[2])); });
      }
      if (freq === 'daily') { for (let d = 1; d <= daysInMonth; d++) addUnique(d); }
      else if (freq === 'weekly') {
        let cur = new Date(calYear, calMonth, 1);
        const dueDay = due.getDay();
        while (cur.getDay() !== dueDay) cur.setDate(cur.getDate() + 1);
        while (cur.getMonth() === calMonth && cur.getFullYear() === calYear) { addUnique(cur.getDate()); cur.setDate(cur.getDate() + 7); }
      } else if (freq === 'monthly') { addUnique(Math.min(due.getDate(), daysInMonth)); }
      else if (freq === 'quarterly') { if ((calMonth - due.getMonth() + 12) % 12 % 3 === 0) addUnique(Math.min(due.getDate(), daysInMonth)); }
      else if (freq === 'annual') { if (due.getMonth() === calMonth) addUnique(Math.min(due.getDate(), daysInMonth)); }
      else { const [y, m] = b.next_due_date.split('-').map(Number); if (y === calYear && m === calMonth + 1) addUnique(parseInt(b.next_due_date.split('-')[2])); }
    });
    return map;
  }, [bills, calMonth, calYear, daysInMonth]);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <Button variant="outline" size="sm" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }} className="h-8 w-8 p-0"><ChevronLeft className="h-4 w-4" /></Button>
        <h3 className="font-semibold text-base capitalize">{monthName}</h3>
        <Button variant="outline" size="sm" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }} className="h-8 w-8 p-0"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} className="min-h-[85px]" />;
          const dayBills = billsByDay[day] || [];
          const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = dateStr === todayStr;
          return (
            <div key={day} className={`min-h-[85px] border rounded p-1 text-xs ${isToday ? 'bg-blue-50 border-blue-400' : 'border-gray-100'}`}>
              <div className={`font-medium mb-0.5 ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>{day}</div>
              {dayBills.map(b => {
                const isPaid = b.paid_dates && b.paid_dates[dateStr];
                const unit = unitMap[b.unit_id];
                const isMyAlert = b.item_type === 'my_alert';
                const isAlert = b.item_type === 'alert' || isMyAlert;
                const style = unit ? { backgroundColor: unit.bg_color, color: unit.text_color, borderLeft: `3px solid ${unit.border_color}` } : {};
                return (
                  <div key={b.id + dateStr} onClick={() => onClickBill(b)}
                    className={`truncate px-1 py-0.5 mb-0.5 cursor-pointer text-[10px] leading-tight ${
                      isMyAlert ? 'rounded-full border border-dashed border-indigo-400 bg-indigo-50 text-indigo-700' :
                      isAlert ? 'rounded-full border border-dashed border-purple-400 bg-purple-50 text-purple-700' :
                      isPaid ? 'rounded bg-green-100 text-green-700' :
                      !unit ? (dateStr < todayStr ? 'rounded bg-red-100 text-red-700' : 'rounded bg-yellow-100 text-yellow-800') : 'rounded'
                    }`}
                    style={isPaid || isAlert ? {} : (unit ? style : {})}
                    title={`${b.name}${b.value ? ` - $${b.value}` : ''}${unit ? ` (${unit.name})` : ''}${isPaid ? ' (PAGO)' : ''}`}
                  >
                    {isPaid ? '✅' : isMyAlert ? (MY_ALERT_ICONS[b.category] || '📅') : isAlert ? (ALERT_ICONS[b.category] || '⚠️') : (CATEGORY_ICONS[b.category] || '')} {b.name}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ====== Alert Categories ======
const ALERT_CATEGORIES = [
  'Seguro Veicular', 'Seguro Empresa', 'Licença', 'Certificado', 'Contrato',
  'Registro Veículo', 'Documento PM', 'Garantia', 'Inspeção', 'Outros'
];

const ALERT_ICONS = {
  'Seguro Veicular': '🚗', 'Seguro Empresa': '🏢', 'Licença': '📜', 'Certificado': '🎓', 'Contrato': '📑',
  'Registro Veículo': '🪪', 'Documento PM': '👤', 'Garantia': '🛡️', 'Inspeção': '🔍', 'Outros': '⚠️'
};

// ====== Main Component ======
const CompanyBillsTab = ({ onUpdate, onOpenFile }) => {
  const [bills, setBills] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitFilter, setUnitFilter] = useState('all');
  const [viewFilter, setViewFilter] = useState('all');
  const [viewItem, setViewItem] = useState(null); // item sendo visualizado
  const [form, setForm] = useState({ name: '', category: 'Outros', value: '', frequency: 'monthly', due_day: 1, next_due_date: '', notes: '', unit_id: '' });
  const [alertForm, setAlertForm] = useState({ name: '', category: 'Outros', expiry_date: '', notes: '', unit_id: '', notify_days: '7,30' });
  const [alertPendingFiles, setAlertPendingFiles] = useState([]);
  const [alertExistingFiles, setAlertExistingFiles] = useState([]);
  const [unitForm, setUnitForm] = useState({ name: '', bg_color: '#FEF3C7', text_color: '#92400E', border_color: '#F59E0B' });
  // Reconciliação Conta a Pagar <-> Seguro vinculado
  const [reconcile, setReconcile] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // conta vinculada aguardando escolha de exclusão
  const [deleting, setDeleting] = useState(false);

  const loadBills = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/company-bills`);
      setBills(data || []);
    } catch (e) { toast.error('Erro ao carregar contas'); } finally { setLoading(false); }
  };

  const loadUnits = async () => {
    try { const { data } = await axios.get(`${API}/admin/company-units`); setUnits(data || []); } catch (e) {}
  };

  const loadAlerts = async () => {
    try { const { data } = await axios.get(`${API}/admin/company-alerts`); setAlerts(data || []); } catch (e) {}
  };

  const loadMyAlerts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/alerts`, { params: { status: 'pendente' } });
      setMyAlerts(data.alerts || []);
    } catch (e) {}
  };

  useEffect(() => { loadBills(); loadUnits(); loadAlerts(); loadMyAlerts(); }, []);

  const filteredBills = unitFilter === 'all' ? bills : bills.filter(b => (b.unit_id || '') === (unitFilter === 'none' ? '' : unitFilter));

  const getDaysUntilDue = (dateStr) => {
    if (!dateStr) return 999;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.ceil((new Date(dateStr + 'T12:00:00') - today) / 86400000);
  };

  const getStatusBadge = (bill) => {
    const days = getDaysUntilDue(bill.next_due_date);
    if (days < 0) return <Badge className="bg-red-600 text-white">Vencida ({Math.abs(days)}d)</Badge>;
    if (days <= 3) return <Badge className="bg-red-500 text-white">Vence em {days}d</Badge>;
    if (days <= 7) return <Badge className="bg-yellow-500 text-white">Vence em {days}d</Badge>;
    if (days <= 15) return <Badge className="bg-blue-500 text-white">Vence em {days}d</Badge>;
    return <Badge className="bg-green-600 text-white">OK ({days}d)</Badge>;
  };

  const formatMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : '-';
  const freqLabel = (f) => FREQUENCIES.find(x => x.value === f)?.label || f;
  const getUnitName = (uid) => units.find(u => u.id === uid)?.name || '';

  const openNew = () => { setEditingBill(null); setForm({ name: '', category: 'Outros', value: '', frequency: 'monthly', due_day: 1, next_due_date: '', notes: '', unit_id: '' }); setShowModal(true); };
  const openEdit = (bill) => { setEditingBill(bill); setForm({ name: bill.name, category: bill.category, value: bill.value, frequency: bill.frequency, due_day: bill.due_day, next_due_date: bill.next_due_date, notes: bill.notes || '', unit_id: bill.unit_id || '' }); setShowModal(true); };

  const saveBill = async () => {
    if (!form.name || !form.value || !form.next_due_date) { toast.error('Preencha nome, valor e data'); return; }
    try {
      if (editingBill) {
        await axios.put(`${API}/admin/company-bills/${editingBill.id}`, form);
        // Reconciliação: se a conta está vinculada a um seguro e valor/vencimento divergem, abrir modal
        const linked = editingBill.linked_insurance;
        if (linked) {
          const newVal = form.value === '' ? 0 : Number(form.value);
          const newDate = form.next_due_date || '';
          const insAmt = (linked.amount === null || linked.amount === undefined) ? null : Number(linked.amount);
          const insDate = linked.expiration_date || '';
          const amountMismatch = Number(insAmt || 0) !== newVal;
          const dateMismatch = (insDate || '') !== (newDate || '');
          if (amountMismatch || dateMismatch) {
            toast.success('Conta atualizada');
            setShowModal(false);
            setReconcile({
              insuranceId: linked.id,
              insLabel: linked.label || 'Seguro',
              billId: editingBill.id,
              billName: form.name,
              billForm: { ...form },
              amountMismatch, dateMismatch,
              insAmt: insAmt === null ? '' : insAmt,
              insDate,
              billVal: newVal,
              billDate: newDate,
              amountChoice: 'bill', amountCustom: '',
              dateChoice: 'bill', dateCustom: '',
            });
            loadBills(); onUpdate && onUpdate();
            return;
          }
        }
        toast.success('Conta atualizada');
      }
      else { await axios.post(`${API}/admin/company-bills`, form); toast.success('Conta criada'); }
      setShowModal(false); loadBills(); onUpdate && onUpdate();
    } catch (e) { toast.error('Erro ao salvar'); }
  };

  const applyReconcile = async () => {
    if (!reconcile) return;
    setReconciling(true);
    try {
      const r = reconcile;
      let finalAmount;
      if (!r.amountMismatch) finalAmount = (r.billVal === '' ? null : Number(r.billVal));
      else if (r.amountChoice === 'ins') finalAmount = (r.insAmt === '' ? 0 : Number(r.insAmt));
      else if (r.amountChoice === 'bill') finalAmount = (r.billVal === '' ? 0 : Number(r.billVal));
      else finalAmount = (r.amountCustom === '' ? 0 : Number(r.amountCustom));
      let finalDate;
      if (!r.dateMismatch) finalDate = r.billDate || '';
      else if (r.dateChoice === 'ins') finalDate = r.insDate || '';
      else if (r.dateChoice === 'bill') finalDate = r.billDate || '';
      else finalDate = r.dateCustom || '';
      // Atualiza o seguro vinculado
      await axios.put(`${API}/admin/insurances/${r.insuranceId}`, { amount: finalAmount, expiration_date: finalDate });
      // Atualiza a conta (envia o form completo p/ não apagar campos)
      const dueDay = finalDate ? (Number(finalDate.slice(8, 10)) || r.billForm.due_day || 1) : (r.billForm.due_day || 1);
      await axios.put(`${API}/admin/company-bills/${r.billId}`, { ...r.billForm, value: finalAmount, next_due_date: finalDate, due_day: dueDay });
      toast.success('Valores sincronizados entre Conta a Pagar e Seguro');
      setReconcile(null);
      loadBills(); onUpdate && onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao sincronizar'); } finally { setReconciling(false); }
  };

  const deleteBill = async (bill) => {
    if (bill.linked_insurance) { setDeleteTarget(bill); return; }
    if (!window.confirm('Excluir esta conta?')) return;
    try { await axios.delete(`${API}/admin/company-bills/${bill.id}`); toast.success('Excluída'); loadBills(); onUpdate && onUpdate(); } catch (e) { toast.error('Erro'); }
  };

  const confirmDelete = async (alsoInsurance) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/company-bills/${deleteTarget.id}`, { params: { delete_insurance: alsoInsurance } });
      toast.success(alsoInsurance ? 'Conta e seguro excluídos' : 'Conta excluída e seguro desvinculado');
      setDeleteTarget(null); loadBills(); onUpdate && onUpdate();
    } catch (e) { toast.error('Erro ao excluir'); } finally { setDeleting(false); }
  };

  const markPaid = async (id) => {
    try { await axios.post(`${API}/admin/company-bills/${id}/mark-paid`); toast.success('Pago!'); loadBills(); onUpdate && onUpdate(); } catch (e) { toast.error('Erro'); }
  };

  const saveUnit = async () => {
    if (!unitForm.name) { toast.error('Nome obrigatório'); return; }
    try {
      if (editingUnit) { await axios.put(`${API}/admin/company-units/${editingUnit.id}`, unitForm); toast.success('Unidade atualizada'); }
      else { await axios.post(`${API}/admin/company-units`, unitForm); toast.success('Unidade criada'); }
      setShowUnitModal(false); setEditingUnit(null); loadUnits();
    } catch (e) { toast.error('Erro'); }
  };

  const deleteUnit = async (id) => {
    if (!window.confirm('Excluir unidade?')) return;
    try { await axios.delete(`${API}/admin/company-units/${id}`); toast.success('Excluída'); loadUnits(); } catch (e) { toast.error('Erro'); }
  };

  // Alert CRUD
  const openNewAlert = () => { setEditingAlert(null); setAlertForm({ name: '', category: 'Outros', expiry_date: '', notes: '', unit_id: '', notify_days: '7,30' }); setAlertPendingFiles([]); setAlertExistingFiles([]); setShowAlertModal(true); };
  const openEditAlert = (a) => { 
    setEditingAlert(a); 
    setAlertForm({ name: a.name, category: a.category, expiry_date: a.expiry_date, notes: a.notes || '', unit_id: a.unit_id || '', notify_days: a.notify_days || '7,30' }); 
    setAlertPendingFiles([]);
    const files = a.files || (a.file_id ? [{ file_id: a.file_id, file_name: a.file_name }] : []);
    setAlertExistingFiles(files);
    setShowAlertModal(true); 
  };

  const saveAlert = async () => {
    if (!alertForm.name || !alertForm.expiry_date) { toast.error('Preencha nome e data de validade'); return; }
    try {
      // Primeiro salvar/criar o alerta com o primeiro arquivo pendente (ou sem arquivo)
      const fd = new FormData();
      fd.append('name', alertForm.name); fd.append('category', alertForm.category);
      fd.append('expiry_date', alertForm.expiry_date); fd.append('notes', alertForm.notes);
      fd.append('unit_id', alertForm.unit_id); fd.append('notify_days', alertForm.notify_days);
      if (alertPendingFiles.length > 0) fd.append('file', alertPendingFiles[0]);
      
      let alertId;
      if (editingAlert) {
        await axios.put(`${API}/admin/company-alerts/${editingAlert.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        alertId = editingAlert.id;
      } else {
        const { data } = await axios.post(`${API}/admin/company-alerts`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        alertId = data.id;
      }
      
      // Upload dos arquivos restantes (2+)
      for (let i = 1; i < alertPendingFiles.length; i++) {
        const extraFd = new FormData();
        extraFd.append('name', alertForm.name); extraFd.append('category', alertForm.category);
        extraFd.append('expiry_date', alertForm.expiry_date); extraFd.append('notes', alertForm.notes);
        extraFd.append('unit_id', alertForm.unit_id); extraFd.append('notify_days', alertForm.notify_days);
        extraFd.append('file', alertPendingFiles[i]);
        await axios.put(`${API}/admin/company-alerts/${alertId}`, extraFd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      
      toast.success(editingAlert ? 'Alerta atualizado' : 'Alerta criado');
      setShowAlertModal(false); loadAlerts(); onUpdate && onUpdate();
    } catch (e) { toast.error('Erro ao salvar alerta'); }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Excluir alerta?')) return;
    try { await axios.delete(`${API}/admin/company-alerts/${id}`); toast.success('Excluído'); loadAlerts(); onUpdate && onUpdate(); } catch (e) { toast.error('Erro'); }
  };

  const renewAlert = async (id) => {
    const newDate = prompt('Nova data de validade (YYYY-MM-DD):');
    if (!newDate) return;
    try { await axios.post(`${API}/admin/company-alerts/${id}/renew`, { new_date: newDate }); toast.success('Renovado!'); loadAlerts(); onUpdate && onUpdate(); } catch (e) { toast.error('Erro'); }
  };

  // Filtros combinados
  const filteredAlerts = (unitFilter === 'all' ? alerts : alerts.filter(a => (a.unit_id || '') === (unitFilter === 'none' ? '' : unitFilter)));

  const overdueAlerts = filteredAlerts.filter(a => getDaysUntilDue(a.expiry_date) < 0);
  const urgentAlerts = filteredAlerts.filter(a => { const d = getDaysUntilDue(a.expiry_date); return d >= 0 && d <= 7; });
  const soonAlerts = filteredAlerts.filter(a => { const d = getDaysUntilDue(a.expiry_date); return d > 7 && d <= 30; });

  const overdue = filteredBills.filter(b => getDaysUntilDue(b.next_due_date) < 0);
  const urgent = filteredBills.filter(b => { const d = getDaysUntilDue(b.next_due_date); return d >= 0 && d <= 3; });
  const soon = filteredBills.filter(b => { const d = getDaysUntilDue(b.next_due_date); return d > 3 && d <= 7; });

  const totalMonthly = filteredBills.reduce((sum, b) => {
    if (b.frequency === 'daily') return sum + (b.value || 0) * 30;
    if (b.frequency === 'monthly') return sum + (b.value || 0);
    if (b.frequency === 'annual') return sum + (b.value || 0) / 12;
    if (b.frequency === 'quarterly') return sum + (b.value || 0) / 3;
    if (b.frequency === 'weekly') return sum + (b.value || 0) * 4.33;
    return sum;
  }, 0);

  // Calcular segunda e domingo desta semana e da próxima (semana = seg-dom)
  const today = new Date(); today.setHours(0,0,0,0);
  const dayOfWeek = today.getDay(); // 0=dom, 1=seg...
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const thisMonday = new Date(today); thisMonday.setDate(today.getDate() + diffToMonday);
  const thisSunday = new Date(thisMonday); thisSunday.setDate(thisMonday.getDate() + 6);
  const nextMonday = new Date(thisMonday); nextMonday.setDate(thisMonday.getDate() + 7);
  const nextSunday = new Date(nextMonday); nextSunday.setDate(nextMonday.getDate() + 6);
  const toStr = (d) => d.toISOString().slice(0,10);

  const thisWeekBills = filteredBills.filter(b => b.next_due_date >= toStr(thisMonday) && b.next_due_date <= toStr(thisSunday));
  const thisWeekTotal = thisWeekBills.reduce((s, b) => s + (b.value || 0), 0);
  const nextWeekBills = filteredBills.filter(b => b.next_due_date >= toStr(nextMonday) && b.next_due_date <= toStr(nextSunday));
  const nextWeekTotal = nextWeekBills.reduce((s, b) => s + (b.value || 0), 0);

  const renderBillRow = (bill) => {
    const unit = units.find(u => u.id === bill.unit_id);
    return (
      <div key={bill.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50" style={unit ? { borderLeftWidth: '4px', borderLeftColor: unit.border_color } : {}}>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setViewItem(bill)}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{CATEGORY_ICONS[bill.category] || '📌'} {bill.name}</span>
            <Badge variant="outline" className="text-xs">{bill.category}</Badge>
            {unit && <Badge className="text-[10px]" style={{ backgroundColor: unit.bg_color, color: unit.text_color }}>{unit.name}</Badge>}
            {bill.linked_insurance && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`bill-linked-insurance-${bill.id}`}><ShieldCheck className="h-3 w-3 mr-0.5 inline" />Seguro</Badge>}
            {getStatusBadge(bill)}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {freqLabel(bill.frequency)} | Vencimento: {formatDate(bill.next_due_date)} | {formatMoney(bill.value)}
          </div>
        </div>
        <div className="flex gap-1 ml-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={() => markPaid(bill.id)} title="Pago" className="h-8 w-8 p-0 text-green-600"><Check className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => openEdit(bill)} title="Editar" className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => deleteBill(bill)} title="Excluir" className="h-8 w-8 p-0 text-red-600"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  };

  const renderAlertRow = (alert) => {
    const unit = units.find(u => u.id === alert.unit_id);
    const days = getDaysUntilDue(alert.expiry_date);
    const statusBadge = days < 0 ? <Badge className="bg-red-600 text-white">Vencido ({Math.abs(days)}d)</Badge>
      : days <= 7 ? <Badge className="bg-red-500 text-white">Vence em {days}d</Badge>
      : days <= 30 ? <Badge className="bg-yellow-500 text-white">Vence em {days}d</Badge>
      : <Badge className="bg-green-600 text-white">OK ({days}d)</Badge>;
    return (
      <div key={alert.id} className="flex items-center justify-between p-3 border-2 border-dashed border-purple-300 rounded-2xl hover:bg-purple-50/50 bg-purple-50/30" style={unit ? { borderColor: unit.border_color } : {}}>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setViewItem({...alert, item_type: 'alert'})}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{ALERT_ICONS[alert.category] || '⚠️'} {alert.name}</span>
            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">{alert.category}</Badge>
            {unit && <Badge className="text-[10px]" style={{ backgroundColor: unit.bg_color, color: unit.text_color }}>{unit.name}</Badge>}
            {statusBadge}
            {alert.file_name && <Badge variant="outline" className="text-[10px] cursor-pointer hover:bg-purple-100" onClick={(e) => { e.stopPropagation(); onOpenFile && onOpenFile(`${API}/uploaded-files/${alert.file_id}`, alert.file_name); }}>📎 {alert.file_name}</Badge>}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Validade: {formatDate(alert.expiry_date)} {alert.notes && `| ${alert.notes}`}
          </div>
        </div>
        <div className="flex gap-1 ml-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={() => renewAlert(alert.id)} title="Renovar" className="h-8 w-8 p-0 text-green-600"><Check className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => openEditAlert(alert)} title="Editar" className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => deleteAlert(alert.id)} title="Excluir" className="h-8 w-8 p-0 text-red-600"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  };

  // Combinar bills e alerts para o calendário
  const calendarBills = viewFilter === 'alerts' ? [] : filteredBills;
  const calendarAlertItems = (viewFilter === 'bills' ? [] : filteredAlerts).map(a => ({
    ...a, next_due_date: a.expiry_date, frequency: 'once', value: 0, item_type: 'alert',
    category: a.category
  }));
  // Alertas do Calendário de Alertas (📅) — sincronizados aqui
  const myAlertsFiltered = unitFilter === 'all' ? myAlerts : (unitFilter === 'none' ? myAlerts : []);
  const calendarMyAlertItems = (viewFilter === 'bills' ? [] : myAlertsFiltered).map(a => ({
    ...a, name: a.title, next_due_date: a.alert_date, frequency: 'once', value: 0,
    item_type: 'my_alert', category: a.category,
  }));
  const calendarItems = [...calendarBills, ...calendarAlertItems, ...calendarMyAlertItems];

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold">Contas a Pagar</h2>
          <p className="text-sm text-gray-500">Custo mensal estimado: <span className="font-semibold text-blue-600">{formatMoney(totalMonthly)}</span></p>
          <div className="flex gap-4 mt-1">
            {thisWeekTotal > 0 && <span className="text-xs text-red-600 font-semibold">Esta semana (pendente): {formatMoney(thisWeekTotal)}</span>}
            {nextWeekTotal > 0 && <span className="text-xs text-yellow-600 font-semibold">Próxima semana: {formatMoney(nextWeekTotal)}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={viewFilter} onChange={e => setViewFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="all">Tudo</option>
            <option value="bills">Só Contas</option>
            <option value="alerts">Só Alertas</option>
          </select>
          <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="all">Todas Unidades</option>
            <option value="none">Sem Unidade</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={() => { setEditingUnit(null); setUnitForm({ name: '', bg_color: '#FEF3C7', text_color: '#92400E', border_color: '#F59E0B' }); setShowUnitModal(true); }}>
            <Settings className="h-4 w-4 mr-1" /> Unidades
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="add-bill-btn">
            <Plus className="h-4 w-4 mr-1" /> Conta
          </Button>
          <Button onClick={openNewAlert} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-1" /> Alerta
          </Button>
        </div>
      </div>

      {/* Calendário */}
      <BillCalendar bills={calendarItems} onClickBill={(b) => setViewItem(b)} units={units} />

      {/* Alertas de validade vencidos/próximos */}
      {viewFilter !== 'bills' && overdueAlerts.length > 0 && <div className="bg-red-50 border border-red-300 rounded-lg p-3"><h3 className="font-semibold text-red-800 mb-2">Alertas Vencidos ({overdueAlerts.length})</h3><div className="space-y-2">{overdueAlerts.map(renderAlertRow)}</div></div>}
      {viewFilter !== 'bills' && urgentAlerts.length > 0 && <div className="bg-orange-50 border border-orange-300 rounded-lg p-3"><h3 className="font-semibold text-orange-800 mb-2">Alertas - Vencem em 7 dias ({urgentAlerts.length})</h3><div className="space-y-2">{urgentAlerts.map(renderAlertRow)}</div></div>}
      {viewFilter !== 'bills' && soonAlerts.length > 0 && <div className="bg-purple-50 border border-purple-300 rounded-lg p-3"><h3 className="font-semibold text-purple-800 mb-2">Alertas - Vencem em 30 dias ({soonAlerts.length})</h3><div className="space-y-2">{soonAlerts.map(renderAlertRow)}</div></div>}

      {/* Contas vencidas/próximas */}
      {viewFilter !== 'alerts' && overdue.length > 0 && <div className="bg-red-50 border border-red-300 rounded-lg p-3"><h3 className="font-semibold text-red-800 mb-2">Contas Vencidas ({overdue.length})</h3><div className="space-y-2">{overdue.map(renderBillRow)}</div></div>}
      {viewFilter !== 'alerts' && urgent.length > 0 && <div className="bg-orange-50 border border-orange-300 rounded-lg p-3"><h3 className="font-semibold text-orange-800 mb-2">Vencem em 3 dias ({urgent.length})</h3><div className="space-y-2">{urgent.map(renderBillRow)}</div></div>}
      {viewFilter !== 'alerts' && soon.length > 0 && <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3"><h3 className="font-semibold text-yellow-800 mb-2">Vencem em 7 dias ({soon.length})</h3><div className="space-y-2">{soon.map(renderBillRow)}</div></div>}

      {/* Lista completa */}
      {viewFilter !== 'alerts' && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Todas as Contas ({filteredBills.length})</h3>
          <div className="space-y-2">{filteredBills.length === 0 ? <p className="text-center text-gray-500 py-8">Nenhuma conta</p> : filteredBills.map(renderBillRow)}</div>
        </div>
      )}
      {viewFilter !== 'bills' && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Todos os Alertas ({filteredAlerts.length})</h3>
          <div className="space-y-2">{filteredAlerts.length === 0 ? <p className="text-center text-gray-500 py-8">Nenhum alerta</p> : filteredAlerts.map(renderAlertRow)}</div>
        </div>
      )}


      {/* Modal Visualização */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {viewItem?.item_type === 'my_alert' ? (MY_ALERT_ICONS[viewItem?.category] || '📅') : viewItem?.item_type === 'alert' ? (ALERT_ICONS[viewItem?.category] || '⚠️') : (CATEGORY_ICONS[viewItem?.category] || '📌')} {viewItem?.name}
            </DialogTitle>
          </DialogHeader>
          {viewItem && (() => {
            const unit = units.find(u => u.id === viewItem.unit_id);
            const isMyAlert = viewItem.item_type === 'my_alert';
            const isAlert = viewItem.item_type === 'alert' || isMyAlert;
            const dateField = isAlert ? (viewItem.expiry_date || viewItem.next_due_date) : viewItem.next_due_date;
            const days = getDaysUntilDue(dateField);
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Categoria:</span><p className="font-semibold">{viewItem.category}</p></div>
                  <div><span className="text-gray-500">{isAlert ? 'Validade:' : 'Vencimento:'}</span><p className="font-semibold">{formatDate(dateField)}</p></div>
                  {!isAlert && <div><span className="text-gray-500">Valor:</span><p className="font-semibold text-orange-600">{formatMoney(viewItem.value)}</p></div>}
                  {!isAlert && <div><span className="text-gray-500">Frequência:</span><p className="font-semibold">{freqLabel(viewItem.frequency)}</p></div>}
                  {unit && <div><span className="text-gray-500">Unidade:</span><p className="font-semibold">{unit.name}</p></div>}
                  <div><span className="text-gray-500">Status:</span><p className={`font-semibold ${days < 0 ? 'text-red-600' : days <= 7 ? 'text-yellow-600' : 'text-green-600'}`}>{days < 0 ? `Vencido há ${Math.abs(days)}d` : `${days} dias restantes`}</p></div>
                </div>
                {viewItem.notes && <div className="text-sm"><span className="text-gray-500">Obs:</span> {viewItem.notes}</div>}
                {(() => {
                  const files = viewItem.files || (viewItem.file_id ? [{ file_id: viewItem.file_id, file_name: viewItem.file_name }] : []);
                  return files.length > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-500">Documentos:</span>
                      <div className="space-y-1 mt-1">
                        {files.map((f, i) => (
                          <div key={f.file_id || i}>
                            <span onClick={() => onOpenFile && onOpenFile(`${API}/uploaded-files/${f.file_id}`, f.file_name)}
                              className="text-blue-600 hover:underline cursor-pointer font-medium">
                              📎 {f.file_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewItem(null)}>Fechar</Button>
            {viewItem?.item_type === 'my_alert' ? (
              <span className="text-xs text-slate-400 self-center">Gerencie em 📅 Alertas</span>
            ) : (
              <Button onClick={() => { const item = viewItem; setViewItem(null); item?.item_type === 'alert' ? openEditAlert(item) : openEdit(item); }} className="bg-blue-600 hover:bg-blue-700">
                <Pencil className="h-4 w-4 mr-1" /> Editar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Criar/Editar Conta */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingBill ? 'Editar Conta' : 'Nova Conta'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Nome</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Aluguel Escritório" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Categoria</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Valor</label>
                <input type="number" step="0.01" className="w-full border rounded px-3 py-2 text-sm" value={form.value} onChange={e => setForm({...form, value: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Frequência</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}>
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Próximo Vencimento</label>
                <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={form.next_due_date} onChange={e => setForm({...form, next_due_date: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Unidade</label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={form.unit_id} onChange={e => setForm({...form, unit_id: e.target.value})}>
                <option value="">Sem unidade</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Observações</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={saveBill} className="bg-blue-600 hover:bg-blue-700">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Gerenciar Unidades */}
      <Dialog open={showUnitModal} onOpenChange={setShowUnitModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Gerenciar Unidades</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Lista de unidades existentes */}
            {units.map(u => (
              <div key={u.id} className="flex items-center justify-between p-2 border rounded" style={{ borderLeftWidth: '4px', borderLeftColor: u.border_color }}>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: u.bg_color, border: `1px solid ${u.border_color}` }} />
                  <span className="font-semibold text-sm">{u.name}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => { setEditingUnit(u); setUnitForm({ name: u.name, bg_color: u.bg_color, text_color: u.text_color, border_color: u.border_color }); }}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600" onClick={() => deleteUnit(u.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Formulário */}
            <div className="border-t pt-3">
              <h4 className="text-sm font-semibold mb-2">{editingUnit ? 'Editar Unidade' : 'Nova Unidade'}</h4>
              <div className="space-y-2">
                <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Nome (ex: Savannah)" value={unitForm.name} onChange={e => setUnitForm({...unitForm, name: e.target.value})} />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500">Fundo</label>
                    <input type="color" className="w-full h-8 border rounded cursor-pointer" value={unitForm.bg_color} onChange={e => setUnitForm({...unitForm, bg_color: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Texto</label>
                    <input type="color" className="w-full h-8 border rounded cursor-pointer" value={unitForm.text_color} onChange={e => setUnitForm({...unitForm, text_color: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Borda</label>
                    <input type="color" className="w-full h-8 border rounded cursor-pointer" value={unitForm.border_color} onChange={e => setUnitForm({...unitForm, border_color: e.target.value})} />
                  </div>
                </div>
                {/* Preview */}
                <div className="rounded px-3 py-2 text-sm" style={{ backgroundColor: unitForm.bg_color, color: unitForm.text_color, borderLeft: `4px solid ${unitForm.border_color}` }}>
                  Preview: {unitForm.name || 'Nome da Unidade'}
                </div>
                <Button onClick={saveUnit} className="w-full bg-blue-600 hover:bg-blue-700">{editingUnit ? 'Atualizar' : 'Criar Unidade'}</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Criar/Editar Alerta */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAlert ? 'Editar Alerta' : 'Novo Alerta de Validade'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Nome</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={alertForm.name} onChange={e => setAlertForm({...alertForm, name: e.target.value})} placeholder="Ex: Seguro F150, Licença Comercial" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Categoria</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={alertForm.category} onChange={e => setAlertForm({...alertForm, category: e.target.value})}>
                  {ALERT_CATEGORIES.map(c => <option key={c} value={c}>{ALERT_ICONS[c]} {c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Data de Validade</label>
                <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={alertForm.expiry_date} onChange={e => setAlertForm({...alertForm, expiry_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Unidade</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={alertForm.unit_id} onChange={e => setAlertForm({...alertForm, unit_id: e.target.value})}>
                  <option value="">Sem unidade</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Alertar (dias antes)</label>
                <input className="w-full border rounded px-3 py-2 text-sm" value={alertForm.notify_days} onChange={e => setAlertForm({...alertForm, notify_days: e.target.value})} placeholder="7,30" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Documentos</label>
              {/* Arquivos já salvos */}
              {alertExistingFiles.length > 0 && (
                <div className="space-y-1 mb-2">
                  {alertExistingFiles.map((f, i) => (
                    <div key={f.file_id || i} className="flex items-center justify-between bg-gray-50 border rounded px-2 py-1">
                      <span className="text-xs text-blue-600 cursor-pointer hover:underline truncate" onClick={() => onOpenFile && onOpenFile(`${API}/uploaded-files/${f.file_id}`, f.file_name)}>
                        📎 {f.file_name}
                      </span>
                      <button onClick={async () => {
                        if (editingAlert) {
                          try {
                            await axios.delete(`${API}/admin/company-alerts/${editingAlert.id}/files/${f.file_id}`);
                            setAlertExistingFiles(prev => prev.filter(x => x.file_id !== f.file_id));
                            loadAlerts(); toast.success('Arquivo removido');
                          } catch (e) { toast.error('Erro ao remover'); }
                        }
                      }} className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0" title="Remover">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Arquivos pendentes (novos, ainda não salvos) */}
              {alertPendingFiles.length > 0 && (
                <div className="space-y-1 mb-2">
                  {alertPendingFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-2 py-1">
                      <span className="text-xs text-green-700 truncate">📄 {f.name}</span>
                      <button onClick={() => setAlertPendingFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0" title="Remover">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input type="file" className="w-full border rounded px-3 py-2 text-sm" value="" onChange={e => { if (e.target.files[0]) { setAlertPendingFiles(prev => [...prev, e.target.files[0]]); e.target.value = ''; } }} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Observações</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={alertForm.notes} onChange={e => setAlertForm({...alertForm, notes: e.target.value})} placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertModal(false)}>Cancelar</Button>
            <Button onClick={saveAlert} className="bg-purple-600 hover:bg-purple-700">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reconciliação Conta a Pagar <-> Seguro */}
      <Dialog open={!!reconcile} onOpenChange={(o) => { if (!o) setReconcile(null); }}>
        <DialogContent className="max-w-md overflow-x-hidden" data-testid="bill-reconcile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><AlertTriangle className="w-4 h-4 text-amber-500" /> Divergências com o Seguro vinculado</DialogTitle>
          </DialogHeader>
          {reconcile && (
            <div className="space-y-3 min-w-0">
              <p className="text-sm text-slate-500">A conta <b>{reconcile.billName}</b> e o seguro <b>{reconcile.insLabel}</b> têm valores diferentes. Escolha o correto (ou digite um novo) — os dois serão igualados.</p>

              {reconcile.amountMismatch && (
                <div className="rounded-lg border p-2.5 space-y-2" data-testid="bill-reconcile-amount">
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Valor</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bill-reconcile-amount-bill">
                    <input type="radio" name="brec-amount" checked={reconcile.amountChoice === 'bill'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'bill' })} />
                    <span>Conta: <b>{formatMoney(reconcile.billVal)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bill-reconcile-amount-ins">
                    <input type="radio" name="brec-amount" checked={reconcile.amountChoice === 'ins'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'ins' })} />
                    <span>Seguro: <b>{formatMoney(reconcile.insAmt || 0)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="brec-amount" checked={reconcile.amountChoice === 'custom'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'custom' })} />
                    <span className="flex items-center gap-1">Outro: <input type="number" step="0.01" className="h-7 w-28 text-sm border rounded px-2" value={reconcile.amountCustom} onFocus={() => setReconcile({ ...reconcile, amountChoice: 'custom' })} onChange={(e) => setReconcile({ ...reconcile, amountCustom: e.target.value, amountChoice: 'custom' })} data-testid="bill-reconcile-amount-custom" /></span>
                  </label>
                </div>
              )}

              {reconcile.dateMismatch && (
                <div className="rounded-lg border p-2.5 space-y-2" data-testid="bill-reconcile-date">
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Vencimento / Validade</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bill-reconcile-date-bill">
                    <input type="radio" name="brec-date" checked={reconcile.dateChoice === 'bill'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'bill' })} />
                    <span>Conta: <b>{formatDate(reconcile.billDate)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bill-reconcile-date-ins">
                    <input type="radio" name="brec-date" checked={reconcile.dateChoice === 'ins'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'ins' })} />
                    <span>Seguro: <b>{formatDate(reconcile.insDate)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="brec-date" checked={reconcile.dateChoice === 'custom'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'custom' })} />
                    <span className="flex items-center gap-1">Outra: <input type="date" className="h-7 w-40 text-sm border rounded px-2" value={reconcile.dateCustom} onFocus={() => setReconcile({ ...reconcile, dateChoice: 'custom' })} onChange={(e) => setReconcile({ ...reconcile, dateCustom: e.target.value, dateChoice: 'custom' })} data-testid="bill-reconcile-date-custom" /></span>
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setReconcile(null)} data-testid="bill-reconcile-cancel-btn">Manter diferentes</Button>
                <Button size="sm" onClick={applyReconcile} disabled={reconciling} className="bg-blue-600 hover:bg-blue-700" data-testid="bill-reconcile-apply-btn">
                  {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Igualar os dois'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Excluir Conta vinculada a Seguro */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="bill-delete-linked-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Conta vinculada a um Seguro</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">A conta <b>{deleteTarget.name}</b> está vinculada ao seguro <b>{deleteTarget.linked_insurance?.label}</b>. O que deseja fazer?</p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => confirmDelete(false)} disabled={deleting} variant="outline" className="justify-start" data-testid="bill-delete-unlink-btn">
                  Desvincular e excluir só a conta (manter o seguro)
                </Button>
                <Button onClick={() => confirmDelete(true)} disabled={deleting} className="justify-start bg-red-600 hover:bg-red-700 text-white" data-testid="bill-delete-both-btn">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Excluir a conta E o seguro
                </Button>
              </div>
              <div className="flex justify-end pt-1">
                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)} data-testid="bill-delete-cancel-btn">Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyBillsTab;
